import { useState } from "react";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";

interface PatientsState {
	loading: boolean;
	error: string | null;
	patients: Patient[];
	patientProfile: patientProfile[];

	getPatientsByPractitioner: (
		practitionerId: string,
		onSuccess?: (patients: Patient[]) => void
	) => void;

	getPatient: (
		patientId: string,
		onSuccess?: (details: Patient) => void
	) => void;

	signupPatient: (
		patient: Patient,
		onSuccess?: (patient: Patient) => void
	) => void;

	getBookingsDetails: (
		patientId: string,
		onSuccess?: (bookings: PatientAppointmentBookingDetails[]) => void
	) => void;

	getSessionBookingsDetails: (
		patientId: string,
		onSuccess?: (bookingsessions: SessionBookingDetails[]) => void
	) => void;

	getAllSessionBookingsDetails: (
		onSuccess?: (bookingsessions: SessionBookingDetails[]) => void
	) => void;

	getAllSessionBookingsDetailsByClinic:(
		clinicId: string,
		onSuccess?: (bookingsessions: SessionBookingDetails[]) => void
	) => void;

	changeSessionBookingStatus: (
		patientId: string,
		bookingSessionId: string,
		status: string,
		onSuccess?: () => void
	) => void;

	getAllPatients: (
		onSuccess?: (patients: Patient[]) => void
	) => void;

	submitConsentForm: (
		formData: any,
		onSuccess?: (res: any) => void
	) => void;

	getPatientDetails: (
		patientId: string,
		onSuccess?: (details: PatientDetails[]) => void
	) => void;

	fetchConsentForm: (
		patientId: string,
		onSuccess?: (response: any) => void
	) => void;

	revokeConsentForm: (
		patientId: string,
		consentFormId: string,
		onSuccess?: (response: any) => void
	) => void;

	patientProfileForm: (
		patientId: string,
		patient: patientProfile,
		onSuccess?: (patient: patientProfile) => void
	) => void;

	updateEnableDisablePatient: (
		patientId: string,
		disabled: boolean,
		onSuccess?: (patient: PatientDetails[]) => void
	) => void;

	deletePatient: (
		patientId: string,
		onSuccess?: () => void
	) => void;

	addCredits: (
		patientId: string,
		credits: string,
		validity: string,
		onSuccess?: () => void
	) => void;
}

const usePatients = (): PatientsState => {
	const [error, setError] = useState<string | null>();
	const [patients, setPatients] = useState<Patient[]>([]);
	const [patientProfile, setPatientProfile] = useState<patientProfile[]>([]);

	const {
		postData: patientSignUpRequest,
		loading: patientSignUpRequestLoading,
	} = useApi<Patient>();

	const {
		getData: getBookingsDetailsRequest,
		loading: getBookingsDetailsRequestLoading,
	} = useApi<PatientAppointmentBookingDetails[]>();

	const {
		getData: getSessionBookingsDetailsRequest,
		loading: getSessionBookingsDetailsRequestLoading,
	} = useApi<SessionBookingDetails[]>();

	const {
		getData: getAllSessionBookingsDetailsByClinicRequest,
		loading: getAllSessionBookingsDetailsByClinicRequestLoading,
	} = useApi<SessionBookingDetails[]>();

	const {
		postData: updatePatientsessionbookingStatusRequest,
		loading: updatePatientsessionbookingStatusRequestLoading,
	} = useApi<SessionBookingDetails[]>();

	const {
		getData: getAllPatientsRequest,
		loading: getAllPatientsRequestLoading,
	} = useApi<Patient[]>();

	const {
		postData: submitConsentFormRequest,
		loading: submitConsentFormRequestLoading,
	} = useApi<any>();

	const {
		getData: getPatientDetailsRequest,
		loading: getPatientDetailsRequestRequestLoading,
	} = useApi<PatientDetails[]>();

	const {
		getData: fetchConsentFormRequest,
		loading: fetchConsentFormRequestLoading,
	} = useApi<any>();

	const {
		postData: revokeConsentFormRequest,
		loading: revokeConsentFormRequestLoading,
	} = useApi<any>();

	const {
		postData: patientProfileRequest,
		loading: patientProfileRequestLoading,
	} = useApi<patientProfile>();

	const {
		postData: updateEnableDisablePatientRequest,
		loading: updateEnableDisablePatientRequestLoading,
	} = useApi<PatientDetails[]>();

	const {
		deleteData: patientDeleteRequest,
		loading: patientDeleteRequestLoading,
	} = useApi<void>();

	const {
		postData: addCreditsRequest,
		loading: addCreditsRequestLoading,
	} = useApi<void>();

	const getPatientsByPractitioner = async (
		practitionerId: string,
		onSuccess?: (patients: Patient[]) => void
	) => {};

	const getPatient = async (
		patientId: string,
		onSuccess?: (details: Patient) => void
	) => {
	};

	const signupPatient = async (
		patient: Patient,
		onSuccess?: (patient: Patient) => void
	) => {
		setError(null);
		await patientSignUpRequest(
			API_ROUTES.PATIENT.signup,
			patient,
			onSuccess,
			setError
		);
	};

	const getBookingsDetails = async (
		patientId: string,
		onSuccess?: (bookings: PatientAppointmentBookingDetails[]) => void
	) => {
		const endPoint = API_ROUTES.APPOINTMENTS.byPatient(patientId);
		await getBookingsDetailsRequest(endPoint, onSuccess, setError);
	};

	const getSessionBookingsDetails = async (
		patientId: string,
		onSuccess?: (bookingsessions: SessionBookingDetails[]) => void
	) => {
		const endPoint = API_ROUTES.APPOINTMENTS.byPatientsessionbookings(patientId);
		await getSessionBookingsDetailsRequest(endPoint, onSuccess, setError);
	};

	const getAllSessionBookingsDetails = async (
		onSuccess?: (bookingsessions: SessionBookingDetails[]) => void
	) => {
		const endPoint = API_ROUTES.APPOINTMENTS.AllPatientsessionbookings();
		await getSessionBookingsDetailsRequest(endPoint, onSuccess, setError);
	};

	const getAllSessionBookingsDetailsByClinic = async (
		clinicId: string,
		onSuccess?: (bookingsessions: SessionBookingDetails[]) => void
	) => {
		const endPoint = API_ROUTES.APPOINTMENTS.allSessionBookingsbyClinic(clinicId);
		await getAllSessionBookingsDetailsByClinicRequest(endPoint, onSuccess, setError);
	};

	const changeSessionBookingStatus = async (
		patientId: string,
		bookingSessionId: string,
		status: string,
		onSuccess?: (bookingsessions: SessionBookingDetails[]) => void
	) => {
		const endPoint = API_ROUTES.APPOINTMENTS.UpdatePatientsessionbookingStatus();
		await updatePatientsessionbookingStatusRequest(endPoint, {patientId, bookingSessionId, status}, onSuccess, setError);
	};

	const getAllPatients = async (
		onSuccess?: (patients: Patient[]) => void
	) => {

		setError(null);
		await getAllPatientsRequest(
		API_ROUTES.PATIENT.base,
		(response) => {
			setPatients(response);
			onSuccess && onSuccess(response);
		},
		setError
		);
	};

	const submitConsentForm = async (
		formData: any,
		onSuccess?: (res: any) => void
	) => {
		setError(null);
		await submitConsentFormRequest(
			API_ROUTES.CONSENT_FORM.submit, // <-- confirm endpoint with me
			{formData}, // will send as JSON
			onSuccess,
			setError
		);
	};

	const getPatientDetails = async (
		patientId: string,
		onSuccess?: (details: PatientDetails[]) => void
	) => {
		setError(null);
		const endPoint = API_ROUTES.PATIENT.getPatientDetails.replace("{{PATIENT_ID}}", patientId);
		await getPatientDetailsRequest(endPoint, onSuccess, setError);
	};

	const fetchConsentForm = async (
		patientId: string,
		onSuccess?: (response: any) => void
	) => {
		setError(null);
		const endPoint = API_ROUTES.PATIENT.fetchConsentForm.replace("{{PATIENT_ID}}", patientId);
		await fetchConsentFormRequest(endPoint, onSuccess, setError);
	}

	const revokeConsentForm = async (
		patientId: string,
		consentFormId: string,
		onSuccess?: (response: any) => void
	) => {
		setError(null);
		const endPoint = API_ROUTES.PATIENT.revokeConsentForm.replace("{{PATIENT_ID}}", patientId);
		await revokeConsentFormRequest(endPoint, {consentFormId}, onSuccess, setError);
	}

	const patientProfileForm = async (
		patientId: string,
		patient: patientProfile,
		onSuccess?: (patient: patientProfile) => void
	) => {
		setError(null);
		await patientProfileRequest(
			API_ROUTES.PATIENT.profile.replace("{{PATIENT_ID}}", patientId || ""),
			patient,
			onSuccess,
			setError
		);
	};

	const updateEnableDisablePatient = async (
		patientId: string,
		disabled: boolean,
		onSuccess?: (patient: PatientDetails[]) => void
	) => {
		setError(null);
		await updateEnableDisablePatientRequest(
			API_ROUTES.PATIENT.enableDisable.replace("{{PATIENT_ID}}", patientId),
			{disabled},
			onSuccess,
			setError
		);
	};

	const deletePatient = async (
		patientId: string,
		onSuccess?: () => void
	) => {
		setError(null);
		const endPoint = API_ROUTES.PATIENT.deletePatient.replace("{{PATIENT_ID}}", patientId);
		await patientDeleteRequest(endPoint, null, onSuccess, setError);
	};

	const addCredits = async (
		patientId: string,
		credits: string,
		validity: string,
		onSuccess?: () => void
	) => {
		setError(null);
		const endPoint = API_ROUTES.PATIENT.addCredits.replace("{{PATIENT_ID}}", patientId);
		await addCreditsRequest(endPoint, {credits, validity}, onSuccess, setError);
	};

	return {
		loading: patientSignUpRequestLoading || getBookingsDetailsRequestLoading || getSessionBookingsDetailsRequestLoading || updatePatientsessionbookingStatusRequestLoading || getAllPatientsRequestLoading || submitConsentFormRequestLoading || getPatientDetailsRequestRequestLoading || fetchConsentFormRequestLoading || revokeConsentFormRequestLoading || getAllSessionBookingsDetailsByClinicRequestLoading || patientProfileRequestLoading || updateEnableDisablePatientRequestLoading || patientDeleteRequestLoading || 
		addCreditsRequestLoading,
		error,
		patients,
		patientProfile,
		getPatientsByPractitioner,
		getPatient,
		signupPatient,
		getBookingsDetails,
		getSessionBookingsDetails,
		getAllSessionBookingsDetails,
		getAllSessionBookingsDetailsByClinic,
		changeSessionBookingStatus,
		getAllPatients,
		submitConsentForm,
		getPatientDetails,
		fetchConsentForm,
		revokeConsentForm,
		patientProfileForm,
		updateEnableDisablePatient,
		deletePatient,
		addCredits,
	};
};

export default usePatients;
